/**
 * Dashboard Selector
 * Shows available dashboards based on user permissions
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DollarSign,
  FileText,
  Users,
  FolderKanban,
  BarChart3,
  LogOut,
} from 'lucide-react';
import { DashboardLabels, DashboardDescriptions, type DashboardTypeValue } from '@bizsolutions/shared';

const dashboardIcons: Record<DashboardTypeValue, React.ReactNode> = {
  billing_finance: <DollarSign className="h-8 w-8" />,
  tax_compliance: <FileText className="h-8 w-8" />,
  customer_operations: <Users className="h-8 w-8" />,
  project_management: <FolderKanban className="h-8 w-8" />,
  analytics: <BarChart3 className="h-8 w-8" />,
};

const dashboardRoutes: Record<DashboardTypeValue, string> = {
  billing_finance: '/billing',
  tax_compliance: '/tax',
  customer_operations: '/customer-operations',
  project_management: '/projects',
  analytics: '/analytics',
};

export function DashboardSelector() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const availableDashboards = (user?.dashboardAccess || []) as DashboardTypeValue[];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-muted/50">
      <header className="bg-background border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">BizSolutions</h1>
            <p className="text-sm text-muted-foreground">
              Welcome back, {user?.firstName} {user?.lastName}
            </p>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-2">Select a Dashboard</h2>
          <p className="text-muted-foreground">
            Choose a dashboard to manage your business operations
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {availableDashboards.map((dashboard) => (
            <Card
              key={dashboard}
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => navigate(dashboardRoutes[dashboard])}
            >
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    {dashboardIcons[dashboard]}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{DashboardLabels[dashboard]}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>{DashboardDescriptions[dashboard]}</CardDescription>
              </CardContent>
            </Card>
          ))}

          {availableDashboards.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="py-8 text-center text-muted-foreground">
                No dashboards available. Please contact your administrator.
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
